import React from 'react';
import { Button } from 'react-bootstrap';
import DiscoveryTooltip from 'DiscoveryTooltip';
import {
  BsExclamationTriangle
} from 'react-icons/bs';
import { FaMask } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { isFileDatasourceType } from 'DataSources/datasourcesApi';

// Lakehouse engines that support discovery (read-only sampling) but not in-place masking.
// Mirrors NON_MASKABLE_ENGINES in c2-privacy-platform-api's OrchestrationJobService — the server
// rejects mutating jobs on these engines (422 ENGINE_NOT_MASKABLE); this only disables the button
// up front so the user sees why instead of clicking through to a worker-side failure.
const NON_MASKABLE_ENGINES = ['hive', 'spark', 'spark_thrift', 'databricks'];
const isNonMaskableEngine = (engine) =>
  !!engine && NON_MASKABLE_ENGINES.includes(String(engine).trim().toLowerCase());

function MaskButton({ connection, datasourceType, engine, dataSourceId, status, errorMessage, fieldQty, prevSearchid, hasMaskingDriver = true, readOnly = false }) {
  const history = useHistory();
  const maskingUnsupported = isNonMaskableEngine(engine);
  const buttonProps = getMaskButtonProps(status, false, fieldQty, hasMaskingDriver, readOnly, maskingUnsupported);

  const getTooltipText = (status) => {
    if (readOnly) {
      return 'This data source is read only.';
    }
    if (maskingUnsupported) {
      return `In-place masking is not supported for ${engine} sources. This engine can be discovered, but its tables cannot be masked in place.`;
    }
    if (!hasMaskingDriver) {
      return isFileDatasourceType(datasourceType)
        ? 'Masking is not available for this file format on the configured masking service.'
        : 'No JDBC driver with masking support is installed for this database type. Add a driver on the JDBC Drivers page.';
    }
    return (status === 'UNSECURED' || status === 'UNMASKED') ?
      'Runs in-place masking on this database. Sensitive fields will be replaced with realistic synthetic data — meant for non-production use.' :
      'Mask database again';
  };

  const MaskAction = () => {
    return (
      <Button
        className='w-100'
        onClick={async () => {
          if (dataSourceId == null) return;
          history.push(`/secure/mask/inventory/${encodeURIComponent(dataSourceId)}`, {
            prevSearchid
          });
        }}
        variant={buttonProps.variant || 'outline-secondary'}
        size='sm'
        disabled={buttonProps.disabled || dataSourceId == null}
      >
        {buttonProps.icon}
        {buttonProps.text}
      </Button>
    );
  }

  const render = errorMessage ? (
    <DiscoveryTooltip text={errorMessage} placement='top'>
      <div>
        <MaskAction />
      </div>
    </DiscoveryTooltip>
  ) : (
    <DiscoveryTooltip text={getTooltipText(status)} placement='top'>
      <div>
        <MaskAction />
      </div>
    </DiscoveryTooltip>
  );

  return render;
}

const getMaskButtonProps = (status, fetchingData, fieldQty, hasMaskingDriver = true, readOnly = false, maskingUnsupported = false) => {
  const classes = 'mr-2';

  if (readOnly) {
    return {
      icon: null,
      text: 'READ ONLY',
      disabled: true
    };
  }

  if (maskingUnsupported) {
    return {
      variant: 'outline-secondary',
      icon: <BsExclamationTriangle className={classes} />,
      text: 'Not Available',
      disabled: true
    };
  }

  if (fetchingData) {
    return {
      variant: 'outline-secondary',
      icon: null,
      text: 'STARTING',
      disabled: true
    }
  }
  if (fieldQty === 0) {
    return {
      variant: 'outline-secondary',
      icon: <BsExclamationTriangle className={classes} />,
      text: 'NO FIELDS',
      disabled: true
    }
  }
  if (!hasMaskingDriver) {
    return {
      variant: 'outline-secondary',
      icon: <BsExclamationTriangle className={classes} />,
      text: 'Not Available',
      disabled: true
    }
  }

  switch (status) {
    case 'unsecured':
    default:
      return {
        variant: 'primary',
        icon: <FaMask className={classes} />,
        text: 'Mask'
      }
    case 'ERROR':
      return {
        // Retry after a failed job — neutral secondary; the row's STATUS cell carries the error.
        variant: 'outline-secondary',
        icon: <BsExclamationTriangle className={classes} />,
        text: 'Mask'
      }
    case 'MASKING':
    case 'PROCESSING':
    case 'RUNNING':
      return {
        variant: 'outline-secondary',
        icon: null,
        text: 'Masking..',
        disabled: true
      }
  }
}
export default MaskButton;